// b24: Platsens inställningar → Värmen vid ankomst och avresa, bara för ägaren.
//
// Två lägen per plats:
//   - Som förut (förvalet): Komfort vid ankomst och Borta vid avresa. Inget ändras för platser som
//     inte väljer något annat.
//   - Egen profil: vid ankomst ett Nobø-läge (Normal, Eko, Komfort) och en eko-temperatur per zon,
//     och vid avresa ett läge (Eko, Borta, Normal) och en eko-temperatur per zon. En knapp sätter
//     samma temperatur för alla zoner, och en zon kan vara "rör inte" så att ingen temperatur
//     skrivs för den.
//
// Kortet visar bara det platsens värmesystem klarar (VARMEFORMAGOR): ett system utan temperatur per
// zon, till exempel Home Assistant, har bara en text om vad Stugkoll kan göra. Zonerna kommer från
// navets signaler, med de namn som Nobø har, och en ny zon får en tom rad. Ändringarna loggas i
// platsens logg.

import {
  type Nobolage,
  type Profilsida,
  type Profilzon,
  type Varmeformaga,
  type Varmeprofil,
  type Varmeprofilvisning,
  ZON_PA_ELEMENTET_TEXT,
  ZONGRANS,
  type Zonstyrning,
} from "@batkoll/gemensam";
import { useEffect, useState } from "preact/hooks";
import { api, Felsvar } from "../api.ts";
import { Kort } from "../komponenter.tsx";

const LAGENAMN: Record<string, string> = {
  normal: "Normal",
  eko: "Eko",
  komfort: "Komfort",
  borta: "Borta",
};

/** En rad i tabellen: zonen, temperaturen som text och "rör inte". */
interface Zonrad {
  namn: string;
  temp: string;
  rorInte: boolean;
  /** Zonen finns i profilen men inte i hubben (längre). */
  saknas: boolean;
  /** Zonen finns i hubben men är inte med i den sparade profilen: en tom rad. */
  ny: boolean;
  /** Vad hubben kan ställa i zonen. `inget`: golvvärme eller termostat på elementet, bara läget styrs. */
  styrning: Zonstyrning | null;
}

interface Sidatillstand {
  lage: Nobolage;
  rader: Zonrad[];
}

function tillSida(
  sparad: Profilsida,
  zoner: Profilzon[],
  forstaGangen: boolean,
  standardLage: Nobolage,
): Sidatillstand {
  const lage = sparad.lage ?? standardLage;
  const rader: Zonrad[] = zoner.map((z) => {
    const v = sparad.zoner[z.namn];
    const finns = z.namn in sparad.zoner;
    return {
      namn: z.namn,
      temp: typeof v === "number" ? String(v) : "",
      rorInte: finns && v === null,
      saknas: false,
      ny: !forstaGangen && !finns,
      styrning: z.styrning,
    };
  });
  for (const [namn, v] of Object.entries(sparad.zoner)) {
    if (zoner.some((z) => z.namn === namn)) continue;
    rader.push({
      namn,
      temp: typeof v === "number" ? String(v) : "",
      rorInte: v === null,
      saknas: true,
      ny: false,
      styrning: null,
    });
  }
  return { lage, rader };
}

/** Sidan som den skickas till molnet: tom eller "rör inte" ger null, annars hela grader. */
function franSida(s: Sidatillstand): Profilsida {
  const zoner: Record<string, number | null> = {};
  for (const r of s.rader) {
    const t = r.temp.trim();
    // En zon med golvvärme eller termostat på elementet skickas inte alls: bara läget styrs.
    if (r.styrning === "inget") continue;
    zoner[r.namn] = r.rorInte || t === "" ? null : Number(t.replace(",", "."));
  }
  return { lage: s.lage, zoner };
}

/** Felet i en sida, på svenska, eller null. Molnet kontrollerar samma sak. */
function sidfel(namn: string, s: Sidatillstand): string | null {
  for (const r of s.rader) {
    if (r.styrning === "inget" || r.rorInte || r.temp.trim() === "") continue;
    const v = Number(r.temp.trim().replace(",", "."));
    if (!Number.isFinite(v)) return `${namn}, ${r.namn}: skriv temperaturen som ett tal.`;
    if (!Number.isInteger(v)) return `${namn}, ${r.namn}: skriv hela grader.`;
    if (v < ZONGRANS.min || v > ZONGRANS.max) {
      return `${namn}, ${r.namn}: temperaturen ska vara ${ZONGRANS.min}–${ZONGRANS.max} °C.`;
    }
  }
  return null;
}

function Lageval(props: {
  id: string;
  etikett: string;
  lagen: string[];
  formaga: Varmeformaga;
  varde: Nobolage;
  andra: (l: Nobolage) => void;
}) {
  const beskrivning = props.formaga.lagen.find((l) => l.id === props.varde)?.text;
  return (
    <label>
      {props.etikett}
      <select
        id={props.id}
        value={props.varde}
        onChange={(e) => props.andra(e.currentTarget.value as Nobolage)}
      >
        {props.lagen.map((l) => (
          <option key={l} value={l}>
            {LAGENAMN[l] ?? l}
          </option>
        ))}
      </select>
      {beskrivning && <span class="liten"> {beskrivning}.</span>}
    </label>
  );
}

function Sida(props: {
  rubrik: string;
  id: string;
  lagen: string[];
  formaga: Varmeformaga;
  zoner: Profilzon[];
  s: Sidatillstand;
  andra: (s: Sidatillstand) => void;
}) {
  const { s } = props;
  const [alla, setAlla] = useState("");
  const hubb = (namn: string) => props.zoner.find((z) => z.namn === namn);
  const sattRad = (namn: string, andring: Partial<Zonrad>) =>
    props.andra({
      ...s,
      rader: s.rader.map((r) => (r.namn === namn ? { ...r, ...andring } : r)),
    });
  const borta = s.lage === "borta";

  return (
    <fieldset class="profilsida">
      <legend>{props.rubrik}</legend>
      <Lageval
        id={`${props.id}-lage`}
        etikett="Nobø-läge"
        lagen={props.lagen}
        formaga={props.formaga}
        varde={s.lage}
        andra={(lage) => props.andra({ ...s, lage })}
      />
      {borta ? (
        <p class="liten">
          Borta är frostskydd, cirka {props.formaga.borta?.min}–{props.formaga.borta?.max} °C.
          Eko-temperaturen används inte och skrivs inte.
        </p>
      ) : (
        <>
          <div class="zonrader">
            {s.rader.map((r) => {
              const z = hubb(r.namn);
              // Golvvärme eller termostat på elementet: hubben rapporterar ett eko-värde, men det
              // gäller inte, så vi visar var det ställs i stället för siffran och inget fält.
              if (r.styrning === "inget") {
                return (
                  <div class="zonrad zon-pa-elementet" key={r.namn} data-zon={r.namn}>
                    <div class="zonnamn">
                      {r.namn}
                      <span class="liten"> · eko på termostaten</span>
                    </div>
                    <p class="liten zonvarning">{ZON_PA_ELEMENTET_TEXT}</p>
                  </div>
                );
              }
              return (
                <div class="zonrad" key={r.namn} data-zon={r.namn}>
                  <div class="zonnamn">
                    {r.namn}
                    {r.ny && <span class="liten"> (ny)</span>}
                    {r.saknas && <span class="liten"> (finns inte i hubben nu)</span>}
                    {z && <span class="liten"> · hubben: eko {z.eko ?? "–"} °C</span>}
                    {r.styrning === "eko" && <span class="liten">, komfort på elementet</span>}
                  </div>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={ZONGRANS.min}
                    max={ZONGRANS.max}
                    step={1}
                    aria-label={`${r.namn}, eko-temperatur ${props.rubrik.toLowerCase()}`}
                    value={r.temp}
                    disabled={r.rorInte}
                    onInput={(e) => sattRad(r.namn, { temp: e.currentTarget.value })}
                  />
                  <label class="kryss">
                    <input
                      type="checkbox"
                      aria-label={`${r.namn}, rör inte ${props.rubrik.toLowerCase()}`}
                      checked={r.rorInte}
                      onChange={(e) => sattRad(r.namn, { rorInte: e.currentTarget.checked })}
                    />
                    Rör inte
                  </label>
                </div>
              );
            })}
          </div>
          {s.rader.length === 0 && (
            <p class="liten">
              Navet har inte sagt några zoner än. De dyker upp när navet hör av sig.
            </p>
          )}
          <div class="knapprad">
            <label>
              Samma temperatur för alla zoner
              <input
                type="number"
                min={ZONGRANS.min}
                max={ZONGRANS.max}
                step={1}
                value={alla}
                aria-label={`Samma temperatur för alla zoner ${props.rubrik.toLowerCase()}`}
                onInput={(e) => setAlla(e.currentTarget.value)}
              />
            </label>
            <button
              type="button"
              class="knapp-andra"
              disabled={alla.trim() === "" || s.rader.length === 0}
              onClick={() =>
                props.andra({
                  ...s,
                  rader: s.rader.map((r) =>
                    r.styrning === "inget" ? r : { ...r, temp: alla.trim(), rorInte: false },
                  ),
                })
              }
            >
              Sätt för alla
            </button>
          </div>
        </>
      )}
    </fieldset>
  );
}

export function Varmeprofildel(props: { plats: string; sparad?: () => Promise<void> }) {
  const [v, setV] = useState<Varmeprofilvisning | null>(null);
  const [typ, setTyp] = useState<"som_forut" | "egen">("som_forut");
  const [ankomst, setAnkomst] = useState<Sidatillstand>({ lage: "normal", rader: [] });
  const [avresa, setAvresa] = useState<Sidatillstand>({ lage: "eko", rader: [] });
  const [fel, setFel] = useState<string | null>(null);
  const [besked, setBesked] = useState<string | null>(null);

  function visa(ny: Varmeprofilvisning) {
    setV(ny);
    const egen = ny.profil.typ === "egen";
    setTyp(ny.profil.typ);
    setAnkomst(tillSida(ny.profil.ankomst, ny.zoner, !egen, "normal"));
    setAvresa(tillSida(ny.profil.avresa, ny.zoner, !egen, "eko"));
  }

  useEffect(() => {
    api
      .varmeprofil(props.plats)
      .then(visa)
      .catch(() => setFel("Värmeprofilen gick inte att hämta."));
  }, [props.plats]);

  if (fel && !v) return null;
  if (!v) return <p class="laddar">Hämtar …</p>;
  // Ett system utan något som styrs (en båt utan nav i huset) har ingen värme att välja för.
  if (v.formagor.length === 0) return null;
  const nobo = v.formagor.find((f) => f.profil !== null);

  async function spara(e: Event) {
    e.preventDefault();
    setFel(null);
    setBesked(null);
    let profil: Varmeprofil;
    if (typ === "egen") {
      const f = sidfel("Vid ankomst", ankomst) ?? sidfel("Vid avresa", avresa);
      if (f) {
        setFel(f);
        return;
      }
      profil = { typ: "egen", ankomst: franSida(ankomst), avresa: franSida(avresa) };
    } else {
      profil = { typ: "som_forut", ankomst: franSida(ankomst), avresa: franSida(avresa) };
    }
    try {
      visa(await api.sattVarmeprofil(props.plats, profil));
      setBesked(
        "Sparat. Nya planer använder det här. Planer som redan finns behåller det de fick.",
      );
      await props.sparad?.().catch(() => {});
    } catch (x) {
      setFel(x instanceof Felsvar ? x.message : "Profilen gick inte att spara.");
    }
  }

  return (
    <Kort rubrik="Värmen vid ankomst och avresa" bred>
      <form class="formular" onSubmit={spara} noValidate>
        <fieldset class="profilval">
          <legend>Vad händer med värmen</legend>
          <label class="radioval">
            <input
              type="radio"
              name="varmeprofil"
              value="som_forut"
              checked={typ === "som_forut"}
              onChange={() => setTyp("som_forut")}
            />{" "}
            Som förut: Komfort vid ankomst och Borta vid avresa
          </label>
          <label class="radioval">
            <input
              type="radio"
              name="varmeprofil"
              value="egen"
              checked={typ === "egen"}
              disabled={!v.egenProfil}
              onChange={() => setTyp("egen")}
            />{" "}
            Egen profil: ett läge och en eko-temperatur per zon
          </label>
        </fieldset>
        {!v.egenProfil && (
          <p class="liten">
            Platsens värmesystem kan inte skriva en temperatur per zon, så här gäller bara "som
            förut".
          </p>
        )}
        {typ === "egen" && nobo?.profil && (
          <>
            <Sida
              rubrik="Vid ankomst"
              id="ankomst"
              lagen={nobo.profil.ankomst}
              formaga={nobo}
              zoner={v.zoner}
              s={ankomst}
              andra={setAnkomst}
            />
            <Sida
              rubrik="Vid avresa"
              id="avresa"
              lagen={nobo.profil.avresa}
              formaga={nobo}
              zoner={v.zoner}
              s={avresa}
              andra={setAvresa}
            />
            <p class="liten">
              Temperaturerna ({ZONGRANS.min}–{ZONGRANS.max} °C, hela grader) skrivs först och läget
              sedan. Stugkoll skriver bara eko-temperaturen, aldrig komfort-temperaturen eller
              veckoprogrammen.
            </p>
          </>
        )}
        {v.formagor.map((f) => (
          <div key={f.system} class="formagor" data-system={f.system}>
            <h3 class="formagorrubrik">{f.namn}</h3>
            <ul class="liten">
              {f.begransningar.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
        ))}
        {fel && (
          <p class="fel" role="alert">
            {fel}
          </p>
        )}
        {besked && (
          <p class="ok-text" role="status">
            {besked}
          </p>
        )}
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Spara värmen
          </button>
        </div>
        <p class="liten">Ändringarna loggas i platsens logg.</p>
      </form>
    </Kort>
  );
}
